import {
  Box,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  getExternalRegisterIdentity,
  getLinkedPatientName,
  isNeverLinkedEntry,
} from "./externalRegisterAdminHelpers";
import type { ExternalRegisterMaintenanceEntry } from "./externalRegisterAdminTypes";

const PatientLinkTooltip = ({ entry }: { entry: ExternalRegisterMaintenanceEntry }) => {
  const { t } = useTranslation();
  const registerIdentity = getExternalRegisterIdentity(entry);
  const linkedPatientName = getLinkedPatientName(entry);
  const neverLinked = isNeverLinkedEntry(entry);

  return (
    <Stack spacing={0.75}>
      <Typography variant="caption" sx={{ fontWeight: 800 }}>
        {t("externalRegister.admin.patientCell.tooltip.title")}
      </Typography>
      <Typography variant="caption">
        {t("externalRegister.admin.patientCell.tooltip.registerEntryPrefix")} <Box component="span" sx={{ fontWeight: 800 }}>{registerIdentity}</Box>.
      </Typography>
      {entry.linkStatus === "linked" ? (
        <Typography variant="caption">
          {t("externalRegister.admin.patientCell.tooltip.linkedPatientPrefix")} <Box component="span" sx={{ fontWeight: 800 }}>{linkedPatientName}</Box>.
        </Typography>
      ) : neverLinked ? (
        <Typography variant="caption">
          {t("externalRegister.admin.patientCell.tooltip.neverLinked")}
        </Typography>
      ) : (
        <Typography variant="caption">
          {t("externalRegister.admin.patientCell.tooltip.unlinked")}
        </Typography>
      )}
      {entry.previousLinkedPatientId ? (
        <Typography variant="caption">
          {t("externalRegister.admin.patientCell.tooltip.previousLinkPrefix")} <Box component="span" sx={{ fontWeight: 800 }}>{entry.previousLinkedPatientId}</Box>.
        </Typography>
      ) : null}
      {entry.unlinkedReason ? (
        <Typography variant="caption">
          {t("externalRegister.admin.patientCell.tooltip.unlinkReason", { reason: entry.unlinkedReason })}
        </Typography>
      ) : null}
    </Stack>
  );
};

export const PatientCell = ({ entry }: { entry: ExternalRegisterMaintenanceEntry }) => {
  const { t } = useTranslation();
  const registerIdentity = getExternalRegisterIdentity(entry);
  const linkedPatientName = getLinkedPatientName(entry);
  const linkedPatientId = entry.linkedPatientId || entry.patientId;
  const neverLinked = isNeverLinkedEntry(entry);

  return (
    <Tooltip title={<PatientLinkTooltip entry={entry} />} arrow placement="right">
      <Stack spacing={0.35} sx={{ minWidth: 0, cursor: "help" }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800, lineHeight: 1.1, textTransform: "uppercase" }}>
            {t("externalRegister.admin.patientCell.captions.registerEntry")}
          </Typography>
          <Box sx={{ borderLeft: "2px solid", borderColor: "divider", pl: 0.75, mt: 0.2, minWidth: 0 }}>
            <Typography variant="body2" noWrap sx={{ fontWeight: 800, lineHeight: 1.2 }}>
              {registerIdentity}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontWeight: 800, lineHeight: 1.1, textTransform: "uppercase" }}>
            {entry.linkStatus === "linked" ? t("externalRegister.admin.patientCell.captions.linkedPatient") : t("externalRegister.admin.patientCell.captions.linkState")}
          </Typography>
          <Box sx={{ borderLeft: "2px solid", borderColor: "divider", pl: 0.75, mt: 0.2, minWidth: 0 }}>
            <Typography variant="caption" noWrap sx={{ display: "block", lineHeight: 1.2 }}>
              {entry.linkStatus === "linked"
                ? `${linkedPatientName}${linkedPatientId ? ` (${linkedPatientId})` : ""}`
                : neverLinked ? t("externalRegister.admin.patientCell.status.neverLinked")
                  : entry.previousLinkedPatientId ? t("externalRegister.admin.patientCell.status.unlinkedPreviously", { patientId: entry.previousLinkedPatientId }) : t("externalRegister.admin.patientCell.status.unlinkedFromPrevious")}
            </Typography>
          </Box>
        </Box>
      </Stack>
    </Tooltip>
  );
};
